/*
 * metrix-popup.js  (v4.7)
 * --------------------------------------------------------------------------
 * Cart-open trigger: runs whenever the cart drawer opens or the customer
 * lands on /cart. Shows qualifying popups in sequence, COVID upgrade first,
 * then C/F offer, then reader-check.
 *
 *   Sequence:
 *     1. COVID-19 → C/F upgrade (dual-button Accept/Decline)
 *          Accept  → swap cart line, chain to next popup
 *          Decline → mark dismissed for session, chain to next popup
 *     2. C/F short-dated informational (single Continue button)
 *          Continue → swap full-price C/F line to short-dated, chain to next
 *          (No decline: customer closes popup and removes item manually.)
 *     3. Reader-check (shown only if cart has no Reader after swaps)
 *          "I have a Gen 2 reader. Continue" → close
 *          "I need a Gen 2 reader"           → /products/metrix®-reader-gen-2
 *
 * Trigger detection:
 *   • On page load, if pathname === '/cart' → run flow.
 *   • Intercept fetch() for /cart/add.js, /cart/change.js, /cart/update.js
 *     and run the flow after each successful mutation (cart drawer has
 *     just opened or is about to).
 *   • window.MetrixPopup.run() exposes a manual trigger for testing.
 *
 * Config (variant IDs, handle patterns) lives in window.METRIX_POPUP_CONFIG,
 * emitted by snippets/metrix-popup.liquid.
 */
(function () {
  'use strict';

  if (!window.METRIX_POPUP_CONFIG) {
    console.warn('[metrix-popup] METRIX_POPUP_CONFIG missing; snippet not rendered?');
    return;
  }

  var CFG = window.METRIX_POPUP_CONFIG;
  var overlay = document.getElementById('MetrixPopupOverlay');
  if (!overlay) {
    console.warn('[metrix-popup] overlay element missing');
    return;
  }

  var modals = overlay.querySelectorAll('[data-popup]');
  var activePopupName = null;
  var pendingOffer = null;  // offer currently awaiting accept/decline/continue
  var runningFlow = false;  // prevent re-entry when multiple cart mutations fire
  var queuedOffers = [];    // remaining offers to show this run
  var flowShownAnyPopup = false;  // whether this flow run actually surfaced a popup
  // v4.7: Track the single variant the customer most recently added. The
  // offer popup targets that variant only — we no longer scan the full
  // cart for matches, which was causing two popups to show when someone
  // had both a COVID item and a C/F item in the cart at once.
  var lastAddedVariantId = null;
  // v4.7: Suppress the flow-after-mutation trigger during internal swaps
  // (the accept-offer flow does its own updateCart + addToCart and
  // should NOT re-open the popup when those fetches resolve).
  var suppressNextFlow = 0;

  /* --------------------------- utilities ---------------------------- */

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function addToCart(variantId, qty) {
    // v4.7: mark this as an internal op so the fetch interceptor does not
    // re-trigger the flow when the response resolves.
    suppressNextFlow++;
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty || 1 })
    }).then(function (r) { return r.json(); });
  }

  function updateCart(updates) {
    // v4.7: mark this as an internal op so the fetch interceptor does not
    // re-trigger the flow when the response resolves.
    suppressNextFlow++;
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  function itemHandleMatches(item, patterns) {
    var h = (item.handle || '').toLowerCase();
    for (var i = 0; i < patterns.length; i++) {
      if (h.indexOf(patterns[i].toLowerCase()) !== -1) return true;
    }
    return false;
  }

  function cartContainsReader(cart) {
    return cart.items.some(function (it) {
      if ((CFG.readerVariantIds || []).indexOf(it.variant_id) !== -1) return true;
      return itemHandleMatches(it, CFG.readerHandlePatterns || []);
    });
  }

  function fireAnalytics(eventName, payload) {
    try {
      if (window.gtag) window.gtag('event', eventName, payload || {});
      if (window.dataLayer) window.dataLayer.push(Object.assign({ event: eventName }, payload || {}));
    } catch (e) { /* best-effort */ }
  }

  /* ------------------------- popup display ------------------------- */

  function showPopup(name) {
    activePopupName = name;
    flowShownAnyPopup = true;
    modals.forEach(function (m) {
      m.style.display = (m.getAttribute('data-popup') === name) ? 'block' : 'none';
    });
    overlay.setAttribute('data-active-popup', name);
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    fireAnalytics('metrix_popup_shown', { popup: name });
  }

  function closePopup() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    activePopupName = null;
    pendingOffer = null;
    runningFlow = false;
    queuedOffers = [];
    flowShownAnyPopup = false;
  }

  // Close popup and send user to /cart. Used when the popup flow completes
  // naturally (all offers handled, reader confirmed, etc.). If this flow
  // run never actually surfaced a popup, there's nothing to "complete" —
  // just close state. This prevents an infinite reload loop on the /cart
  // page when no offers are pending.
  function closeAndGoToCart() {
    var shouldNav = flowShownAnyPopup;
    closePopup();
    if (!shouldNav) return;
    var p = window.location.pathname;
    if (p === '/cart' || p === '/cart/') {
      // Already on cart page — reload so any swaps are reflected.
      window.location.reload();
    } else {
      window.location.href = '/cart';
    }
  }

  function setBusy(on) {
    if (on) overlay.classList.add('metrix-popup__busy');
    else overlay.classList.remove('metrix-popup__busy');
  }

  /* ---------------------- offer selection -------------------------- */
  // Returns array of matches in priority order (COVID upgrades first, then C/F offers).
  // v4.9.1: Transient decline suppression. Set on decline, cleared on the
  // next user-initiated cart-add OR Buy-it-now intercept. So decline silences
  // the popup for the rest of the current /cart visit, but a new add (or a
  // PDP Buy-it-now click after browsing) re-enables the prompt.
  function clearDeclineSuppression() {
    for (var i = 0; i < CFG.offers.length; i++) {
      try { sessionStorage.removeItem(CFG.offers[i].sessionKey); } catch (e) { /* ignore */ }
    }
    // v4.9.3: also drop the just-declined latch so a subsequent /cart load
    // fully re-evaluates without inheriting the prior decline.
    try { sessionStorage.removeItem('metrix_just_declined'); } catch (e) { /* ignore */ }
  }

  function findAllOffersForCart(cart) {
    var results = [];
    for (var i = 0; i < CFG.offers.length; i++) {
      var offer = CFG.offers[i];
      // v4.9.1: transient decline suppression — cleared on next external add.
      if (sessionStorage.getItem(offer.sessionKey) === '1') continue;
      var matched = cart.items.find(function (it) {
        return offer.matchVariantIds.indexOf(it.variant_id) !== -1;
      });
      if (matched) {
        results.push({ offer: offer, lineItem: matched });
      }
    }
    return results;
  }

  // v4.7: Find the single offer that matches a specific variant the user
  // just added. Returns null if none.
  function findOfferForVariant(cart, variantId) {
    if (variantId == null) return null;
    var vid = Number(variantId);
    for (var i = 0; i < CFG.offers.length; i++) {
      var offer = CFG.offers[i];
      // v4.9.1: transient decline suppression (see findAllOffersForCart).
      if (sessionStorage.getItem(offer.sessionKey) === '1') continue;
      if (offer.matchVariantIds.indexOf(vid) === -1) continue;
      var li = cart.items.find(function (it) { return it.variant_id === vid; });
      if (li) return { offer: offer, lineItem: li };
    }
    return null;
  }

  /* --------------------- flow orchestration ------------------------ */

  function runFlow() {
    if (runningFlow) return;
    if (overlay.classList.contains('is-open')) return;  // popup already up
    runningFlow = true;
    // v4.7: If we know the variant the user just added, only consider
    // the offer that matches that one item. Falling back to scanning the
    // whole cart is reserved for the /cart page-load entrypoint, where
    // there's no "just added" event — and even there we only show one
    // popup, not one per qualifying cart line.
    var addedId = lastAddedVariantId;
    lastAddedVariantId = null;  // consume
    fetchCart().then(function (cart) {
      var single = null;
      if (addedId !== null) {
        single = findOfferForVariant(cart, addedId);
      } else {
        var all = findAllOffersForCart(cart);
        if (all.length) single = all[0];
      }
      queuedOffers = single ? [single] : [];
      advanceQueue();
    }).catch(function (err) {
      console.error('[metrix-popup] cart fetch failed', err);
      runningFlow = false;
    });
  }

  function advanceQueue() {
    if (queuedOffers.length === 0) {
      // All offers handled — maybe show reader-check, otherwise flow is
      // complete and we send the user to /cart to review.
      fetchCart().then(function (cart) {
        if (shouldShowReaderCheck(cart)) {
          showPopup('reader-check');
        } else {
          closeAndGoToCart();
        }
      }).catch(function () { closeAndGoToCart(); });
      return;
    }
    var next = queuedOffers.shift();
    pendingOffer = next.offer;
    pendingOffer._lineItem = next.lineItem;
    showPopup(next.offer.popup);
  }

  var READER_CHECK_SESSION_KEY = 'metrix_reader_check_ack';

  function shouldShowReaderCheck(cart) {
    // v4.6: Suppression is ONLY set on "I have a Gen 2 reader" (affirmative
    // click). Anything else — "I need a Gen 2 reader" redirect, X close,
    // backdrop click — leaves the ack flag unset, so the popup continues to
    // trigger on subsequent cart mutations until either (a) the user clicks
    // "Yes, I have a reader", or (b) a reader is already in the cart
    // (covered by the cartContainsReader check below).
    try {
      if (sessionStorage.getItem(READER_CHECK_SESSION_KEY) === '1') return false;
    } catch (e) { /* ignore */ }

    // June 2026: reader-check fires for ANY C/F variant in cart, not just
    // short-dated. The regular C/F 25pk is now a normal-priced purchase path,
    // and customers buying it still need a Gen 2 reader.
    // Falls back to the legacy short-dated-only list if CFG.cfluVariantIds is
    // missing (older theme versions).
    var cfluIds = (CFG.cfluVariantIds && CFG.cfluVariantIds.length)
      ? CFG.cfluVariantIds
      : CFG.offers.map(function (o) { return o.replaceWithVariantId; });
    var hasCflu = cart.items.some(function (it) {
      return cfluIds.indexOf(it.variant_id) !== -1;
    });
    if (!hasCflu) return false;
    return !cartContainsReader(cart);
  }

  function ackReaderCheck() {
    try { sessionStorage.setItem(READER_CHECK_SESSION_KEY, '1'); } catch (e) { /* ignore */ }
  }

  /* --------------------- action handlers --------------------------- */

  function onAcceptOffer() {
    if (!pendingOffer) { advanceQueue(); return; }
    var offer = pendingOffer;
    var li = offer._lineItem;
    setBusy(true);
    fireAnalytics('metrix_popup_offer_accept', {
      popup: offer.popup,
      from_variant: li.variant_id,
      to_variant: offer.replaceWithVariantId,
      quantity: li.quantity
    });

    // Remove original line, add short-dated replacement same qty.
    var updates = {};
    updates[li.variant_id] = 0;
    updateCart(updates)
      .then(function () { return addToCart(offer.replaceWithVariantId, li.quantity); })
      .then(function () {
        setBusy(false);
        // v4.9: if user is sitting on /cart, reload so the page reflects the
        // post-swap cart contents. Otherwise advance the popup queue.
        if (window.location.pathname === '/cart' || window.location.pathname === '/cart/') {
          window.location.reload();
          return;
        }
        advanceQueue();
      })
      .catch(function (e) {
        console.error('[metrix-popup] offer accept failed', e);
        setBusy(false);
        advanceQueue();
      });
  }

  function onDeclineOffer() {
    if (pendingOffer) {
      sessionStorage.setItem(pendingOffer.sessionKey, '1');
      // v4.9.3: mark that the user just declined an offer. The next /cart
      // page-load (whether triggered by closeAndGoToCart from a PDP or the
      // user already being on /cart) will preserve suppression even if the
      // referrer doesn't match /cart. Unset on the first /cart load.
      try { sessionStorage.setItem('metrix_just_declined', '1'); } catch (e) { /* ignore */ }
      fireAnalytics('metrix_popup_offer_decline', { popup: pendingOffer.popup });
    }
    advanceQueue();
  }

  function onReaderHave() {
    fireAnalytics('metrix_popup_reader_have', {});
    ackReaderCheck();
    closeAndGoToCart();
  }

  function onReaderNeed() {
    // v4.6: Do NOT ack the reader check when the user selects "I need a
    // Gen 2 reader". If they come back without a reader in cart, we want
    // the popup to trigger again next time they add a C/F test.
    fireAnalytics('metrix_popup_reader_need', {});
    closePopup();
    window.location.href = CFG.readerUrl || '/products/metrix®-reader-gen-2';
  }

  /* -------------------------- event wiring ------------------------- */

  function onActionClick(e) {
    var btn = e.target.closest('[data-popup-action]');
    if (!btn) return;
    var action = btn.getAttribute('data-popup-action');
    e.preventDefault();
    e.stopPropagation();
    switch (action) {
      case 'close':
        // v4.6: X close on reader-check no longer acks. Popup re-triggers
        // until the user affirmatively clicks "I have a reader" or a reader
        // is added to the cart.
        fireAnalytics('metrix_popup_close', { popup: activePopupName });
        closePopup();
        break;
      case 'accept-offer':  onAcceptOffer();  break;
      case 'decline-offer': onDeclineOffer(); break;
      case 'reader-have':   onReaderHave();   break;
      case 'reader-need':   onReaderNeed();   break;
    }
  }

  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) {
      // v4.9: offer-cflu-1pk and offer-cflu-25pk are non-dismissable
      // (no X close, no backdrop close). Customer must click "Apply" to
      // proceed. All other modals close on backdrop click.
      if (activePopupName === 'offer-cflu-1pk' || activePopupName === 'offer-cflu-25pk') {
        return;
      }
      // v4.6: Backdrop click on reader-check no longer acks. Popup re-triggers
      // until affirmative "I have a reader" click or reader-in-cart.
      fireAnalytics('metrix_popup_backdrop_close', { popup: activePopupName });
      closePopup();
    }
  });
  overlay.addEventListener('click', onActionClick);

  /* ------------------ cart-open trigger detection ------------------ */

  // 1. /cart page load.
  function runIfCartPage() {
    if (window.location.pathname === '/cart' || window.location.pathname === '/cart/') {
      // v4.9.3: Decide whether to clear transient decline suppression.
      //   1. If "metrix_just_declined" flag is set, the user just declined
      //      an offer (possibly on a PDP) and was redirected here by
      //      closeAndGoToCart. Preserve suppression. Consume the flag so
      //      the next /cart visit will follow the normal rules.
      //   2. Otherwise, if document.referrer is /cart (in-place reload, or
      //      closeAndGoToCart from /cart -> /cart), preserve suppression.
      //   3. Otherwise (came from outside /cart -> shopped and came back),
      //      clear suppression so the upgrade prompt re-fires.
      try {
        var justDeclined = sessionStorage.getItem('metrix_just_declined') === '1';
        if (justDeclined) {
          sessionStorage.removeItem('metrix_just_declined');
        } else {
          var ref = document.referrer || '';
          var cameFromCart = /\/cart(?:\/|\?|$)/.test(ref);
          if (!cameFromCart) clearDeclineSuppression();
        }
      } catch (e) { /* ignore */ }
      // Delay slightly to let other DOMContentLoaded scripts settle.
      setTimeout(runFlow, 50);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runIfCartPage);
  } else {
    runIfCartPage();
  }

  // v4.7: Extract the variant id of a /cart/add.js request. Shopify
  // accepts JSON bodies, FormData, and URL-encoded bodies.
  function extractAddedVariantId(init) {
    if (!init || !init.body) return null;
    try {
      if (typeof init.body === 'string') {
        // Try JSON first
        try {
          var parsed = JSON.parse(init.body);
          if (parsed && parsed.id != null) return Number(parsed.id);
        } catch (e) { /* not JSON */ }
        // Fall back to URL-encoded
        var params = new URLSearchParams(init.body);
        var id = params.get('id');
        if (id) return Number(id);
      } else if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
        var fid = init.body.get('id');
        if (fid) return Number(fid);
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  // 2. Cart mutations: monkey-patch window.fetch to detect /cart/add,
  // /cart/change, /cart/update responses and run the flow after each.
  var origFetch = window.fetch ? window.fetch.bind(window) : null;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = '';
      if (typeof input === 'string') url = input;
      else if (input && input.url) url = input.url;
      var isCartAdd = /\/cart\/add(\.js)?(\?|$)/i.test(url);
      var isCartMutation = /\/cart\/(add|change|update)(\.js)?(\?|$)/i.test(url);
      // v4.7: If this call is initiated by our own internal swap logic
      // (addToCart / updateCart), don't re-trigger the flow.
      var internal = false;
      if (isCartMutation && suppressNextFlow > 0) {
        suppressNextFlow--;
        internal = true;
      }
      // v4.7: For user-initiated adds, capture the variant id so the
      // next flow run knows exactly which item just hit the cart.
      if (isCartAdd && !internal) {
        var vid = extractAddedVariantId(init);
        if (vid !== null) lastAddedVariantId = vid;
        // v4.9.1: a fresh user add resets transient decline suppression.
        clearDeclineSuppression();
      }
      var p = origFetch(input, init);
      if (isCartMutation && !internal) {
        p.then(function () {
          // Let the drawer finish opening (Shopify themes typically open
          // the drawer after the fetch resolves), then trigger flow.
          setTimeout(runFlow, 150);
        }).catch(function () { /* ignore */ });
      }
      return p;
    };
  }

  // 3. Also listen for XHR-based cart mutations (older themes).
  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._metrixUrl = url || '';
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    var self = this;
    var url = self._metrixUrl || '';
    var isCartAdd = /\/cart\/add(\.js)?(\?|$)/i.test(url);
    var isCartMutation = /\/cart\/(add|change|update)(\.js)?(\?|$)/i.test(url);
    // v4.7: honor suppression flag for internal ops (though internal ops
    // use fetch, not XHR — still paying the suppression cost here to be
    // safe against stray consumers).
    var internal = false;
    if (isCartMutation && suppressNextFlow > 0) {
      suppressNextFlow--;
      internal = true;
    }
    if (isCartAdd && !internal) {
      try {
        var vid = extractAddedVariantId({ body: body });
        if (vid !== null) lastAddedVariantId = vid;
      } catch (e) { /* ignore */ }
      // v4.9.1: fresh user add resets transient decline suppression.
      clearDeclineSuppression();
    }
    if (isCartMutation && !internal) {
      self.addEventListener('loadend', function () {
        setTimeout(runFlow, 150);
      });
    }
    return origSend.apply(this, arguments);
  };

  // Clean up any stale flag from prior versions.
  try { sessionStorage.removeItem('metrix_popup_flow_pass'); } catch (e) { /* ignore */ }

  /* ----- v4.9: Empty-cart Buy-it-now / Checkout intercept on PDPs ----- */
  // Today on a PDP with empty cart, clicking the dynamic checkout / "Buy
  // it now" / explicit /checkout link does nothing useful — Shopify's
  // /checkout endpoint redirects to home when the cart is empty. v4.9
  // makes that flow do the obvious thing: silently add the current PDP
  // variant to cart, then send the customer to /cart so the upgrade
  // popup chain (and reader-check) fires there on cart-open.
  function getPdpVariantId() {
    // 1. Hidden id input on the standard product form (Dawn).
    var hidden = document.querySelector('form[action="/cart/add"] input[name="id"], form[action^="/cart/add"] input[name="id"]');
    if (hidden && hidden.value) return Number(hidden.value);
    // 2. Dawn variant-radios block.
    var sel = document.querySelector('input[name^="options"][checked], input[name="id"]');
    if (sel && sel.value && /^\d+$/.test(sel.value)) return Number(sel.value);
    // 3. Shopify analytics meta (best-effort, may not be present).
    try {
      var p = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      if (p && p.variants && p.variants[0]) return Number(p.variants[0].id);
    } catch (e) { /* ignore */ }
    return null;
  }

  function isCheckoutTrigger(target) {
    if (!target) return false;
    // Direct /checkout links (some themes use a plain anchor).
    if (target.tagName === 'A' && target.getAttribute('href') &&
        /^\/?checkout(?:\b|\?|\/)/.test(target.getAttribute('href'))) {
      return true;
    }
    // Shopify's dynamic payment button (rendered by {{ form | payment_button }}).
    if (target.classList && (
        target.classList.contains('shopify-payment-button__button') ||
        target.classList.contains('shopify-payment-button__button--unbranded')
    )) return true;
    // Generic any-button-with-name=checkout (some themes use this pattern).
    if (target.getAttribute && target.getAttribute('name') === 'checkout') return true;
    return false;
  }

  function onPossibleCheckoutClick(e) {
    var trigger = e.target.closest('a, button');
    while (trigger) {
      if (isCheckoutTrigger(trigger)) break;
      trigger = trigger.parentElement && trigger.parentElement.closest && trigger.parentElement.closest('a, button');
    }
    if (!trigger) return;
    // Only on PDPs.
    var variantId = getPdpVariantId();
    if (!variantId) return;
    // Only step in if cart is currently empty. Otherwise let normal
    // checkout flow proceed (popup will fire on cart-open at /cart).
    e.preventDefault();
    e.stopPropagation();
    fetchCart().then(function (cart) {
      if (cart.item_count > 0) {
        // Cart has items — just go to /cart and let the popup chain run.
        window.location.href = '/cart';
        return;
      }
      // Empty cart — silent add then route to /cart.
      // v4.9.1: explicit Buy-it-now click is a fresh purchase intent —
      // clear any transient decline suppression so the upgrade popup re-fires
      // on /cart after the silent add.
      clearDeclineSuppression();
      suppressNextFlow++;  // popup will fire on /cart, not on PDP
      lastAddedVariantId = variantId;

      addToCart(variantId, (function() {
        var q= document.querySelector('form[action^="/cart/add"] input[name="quantity"]');
        var n= q ? parseInt(q.value,10) : 0;
        return n > 0 ? n : 1;
      })()).then(function () {
        window.location.href = '/cart';
      }).catch(function (err) {
        console.error('[metrix-popup] empty-cart checkout add failed', err);
        // Fall back to original button intent (re-fire the click programmatically).
        if (trigger.tagName === 'A' && trigger.href) {
          window.location.href = trigger.href;
        }
      });
    });
  }
  document.addEventListener('click', onPossibleCheckoutClick, true);

  // Debug / manual trigger
  window.MetrixPopup = {
    run: runFlow,
    show: showPopup,
    close: closePopup,
    findAllOffersForCart: findAllOffersForCart,
    fetchCart: fetchCart,
    config: CFG
  };
})();
